import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';

const PreviewContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  z-index: 2000;
`;

const PreviewHeader = styled.div`
  background: #1f2937;
  color: white;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PreviewTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const PreviewControls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const DeviceSelector = styled.div`
  display: flex;
  gap: 8px;
  background: #374151;
  padding: 4px;
  border-radius: 6px;
`;

const DeviceButton = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  background: ${props => props.active ? '#3b82f6' : 'transparent'};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${props => props.active ? '#3b82f6' : '#4b5563'};
  }
`;

const PreviewActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover {
            background: #2563eb;
          }
        `;
      default:
        return `
          background: #6b7280;
          color: white;
          &:hover {
            background: #4b5563;
          }
        `;
    }
  }}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #374151;
  }
`;

const PreviewBody = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  overflow: hidden;
`;

const DeviceFrame = styled.div<{ device: DeviceType }>`
  background: white;
  border-radius: ${props => {
    switch (props.device) {
      case 'mobile': return '24px';
      case 'tablet': return '12px';
      default: return '8px';
    }
  }};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  transition: all 0.3s ease-in-out;
  overflow: hidden;

  ${props => {
    switch (props.device) {
      case 'desktop':
        return `
          width: 1200px;
          height: 80vh;
          min-height: 600px;
        `;
      case 'tablet':
        return `
          width: 768px;
          height: 1024px;
          max-height: 80vh;
        `;
      case 'mobile':
        return `
          width: 375px;
          height: 667px;
          max-height: 80vh;
        `;
    }
  }}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: ${props => props.device === 'mobile' ? '20px' : '0'};
    background: #1f2937;
    border-radius: ${props => props.device === 'mobile' ? '24px 24px 0 0' : '0'};
    z-index: 10;
  }
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6b7280;
  font-size: 14px;
  z-index: 5;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #ef4444;
  text-align: center;
  padding: 40px;
  z-index: 5;

  h4 {
    margin: 0 0 8px 0;
    color: #1f2937;
  }
`;

const DeviceInfo = styled.div`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  z-index: 10;
`;

type DeviceType = 'desktop' | 'tablet' | 'mobile';

interface Device {
  type: DeviceType;
  name: string;
  icon: string;
  width: number;
  height?: number;
}

const devices: Device[] = [
  { type: 'desktop', name: 'Desktop', icon: '🖥️', width: 1200 },
  { type: 'tablet', name: 'Tablet', icon: '📱', width: 768, height: 1024 },
  { type: 'mobile', name: 'Mobile', icon: '📱', width: 375, height: 667 },
];

interface ResponsivePreviewProps {
  isOpen: boolean;
  pageTitle: string;
  htmlContent: string;
  onClose: () => void;
  onRefresh?: () => void;
}

export const ResponsivePreview: React.FC<ResponsivePreviewProps> = ({
  isOpen,
  pageTitle,
  htmlContent,
  onClose,
  onRefresh,
}) => {
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>('desktop');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (isOpen && htmlContent) {
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, htmlContent, selectedDevice]);

  const loadPreview = () => {
    setIsLoading(true);
    setError(null);

    try {
      if (iframeRef.current) {
        const iframe = iframeRef.current;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        
        if (doc) {
          doc.open();
          doc.write(getPreviewHtml());
          doc.close();
          
          // Handle iframe load
          iframe.onload = () => {
            setIsLoading(false);
          };

          // Handle iframe errors
          iframe.onerror = () => {
            setError('Failed to load preview');
            setIsLoading(false);
          };
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview error');
      setIsLoading(false);
    }
  };

  const getPreviewHtml = () => {
    const currentDevice = devices.find(d => d.type === selectedDevice);
    
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${pageTitle} - Preview</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            width: 100%;
            min-height: 100vh;
            overflow-x: hidden;
          }
          img {
            max-width: 100%;
            height: auto;
          }
          .container {
            max-width: ${currentDevice?.width || 1200}px;
            margin: 0 auto;
            padding: 0 16px;
          }
          
          /* Responsive utilities */
          @media (max-width: 768px) {
            .container {
              padding: 0 12px;
            }
          }
          
          /* Preview-specific styles */
          .preview-badge {
            position: fixed;
            top: 10px;
            right: 10px;
            background: #3b82f6;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            z-index: 9999;
            opacity: 0.8;
          }
        </style>
      </head>
      <body>
        <div class="preview-badge">${currentDevice?.name} Preview</div>
        ${htmlContent}
        
        <script>
          // Prevent form submissions in preview
          document.addEventListener('submit', function(e) {
            e.preventDefault();
            alert('Form submission disabled in preview mode');
          });
          
          // Handle link clicks
          document.addEventListener('click', function(e) {
            if (e.target.tagName === 'A' && e.target.href) {
              e.preventDefault();
              if (e.target.href.startsWith('#')) {
                // Allow anchor links to work
                const target = document.querySelector(e.target.href);
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth' });
                }
              } else {
                alert('External links disabled in preview mode');
              }
            }
          });
          
          // Smooth scrolling for anchor links
          document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function (e) {
              e.preventDefault();
              const target = document.querySelector(this.getAttribute('href'));
              if (target) {
                target.scrollIntoView({ behavior: 'smooth' });
              }
            });
          });
        </script>
      </body>
      </html>
    `;
  };

  const handleDeviceChange = (device: DeviceType) => {
    setSelectedDevice(device);
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      loadPreview();
    }
  };

  const currentDevice = devices.find(d => d.type === selectedDevice);

  if (!isOpen) return null;

  return (
    <PreviewContainer>
      <PreviewHeader>
        <PreviewTitle>{pageTitle} - Responsive Preview</PreviewTitle>
        <PreviewControls>
          <DeviceSelector>
            {devices.map((device) => (
              <DeviceButton
                key={device.type}
                active={selectedDevice === device.type}
                onClick={() => handleDeviceChange(device.type)}
              >
                <span>{device.icon}</span>
                {device.name}
              </DeviceButton>
            ))}
          </DeviceSelector>
          <PreviewActions>
            <ActionButton onClick={handleRefresh}>
              🔄 Refresh
            </ActionButton>
            <CloseButton onClick={onClose}>×</CloseButton>
          </PreviewActions>
        </PreviewControls>
      </PreviewHeader>

      <PreviewBody>
        <DeviceFrame device={selectedDevice}>
          {currentDevice && (
            <DeviceInfo>
              {currentDevice.width}px
              {currentDevice.height && ` × ${currentDevice.height}px`}
            </DeviceInfo>
          )}
          
          {isLoading && (
            <LoadingOverlay>
              Loading preview...
            </LoadingOverlay>
          )}
          
          {error && (
            <ErrorOverlay>
              <h4>Preview Error</h4>
              <p>{error}</p>
              <ActionButton 
                variant="primary" 
                onClick={handleRefresh}
                style={{ marginTop: '16px' }}
              >
                Try Again
              </ActionButton>
            </ErrorOverlay>
          )}
          
          <PreviewIframe
            ref={iframeRef}
            title={`${pageTitle} - ${currentDevice?.name} Preview`}
            sandbox="allow-scripts allow-same-origin"
          />
        </DeviceFrame>
      </PreviewBody>
    </PreviewContainer>
  );
};